// src/pages/CategoryPage.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useProductData } from "../context/ProductDataContext";

import CategoryGrid from "../components/category_page/CategoryGrid";
import ProductGrid from "../components/category_page/ProductGrid";
import FilterPanel from "../components/category_page/FilterPanel";
import CategoryBreadcrumb from "../components/category_page/CategoryBreadcrumb";
import { categoryCanonical } from "../lib/seo";
import { activeFacetCount, facetsFromSearchParams, resolveCategoryAttributes, writeFacetsToSearchParams } from "../lib/productAttributes";

function findCategoryById(id, list = []) {
  if (!id) return null;
  for (const item of list || []) {
    if (item.id === id) return item;
    if (Array.isArray(item.children) && item.children.length) {
      const found = findCategoryById(id, item.children);
      if (found) return found;
    }
  }
  return null;
}

export default function CategoryPage() {
  const navigate = useNavigate();
  const { categories, loading, attributeConfig, attributeProfiles } = useProductData();
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const [activeFilters, setActiveFilters] = useState({ facets: {} });
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(false);
  const urlReady = useRef(false);
  const skipNextUrlWrite = useRef(false);
  const lastWrittenSearch = useRef(null);

  useEffect(() => {
    const sync = () => setDesktopFiltersOpen(window.innerWidth >= 1024);
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, []);

  const currentCategory = useMemo(
    () => (categoryId ? findCategoryById(categoryId, categories) : null),
    [categoryId, categories]
  );

  const searchKey = searchParams.toString();

  useEffect(() => {
    if (lastWrittenSearch.current === searchKey) {
      lastWrittenSearch.current = null;
      return;
    }

    const params = new URLSearchParams(searchKey);
    const allFlag = params.get("all") === "1";
    const nextFunctionIds = (params.get("functions") || "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    const isLeafCategory =
      !!(currentCategory && !(Array.isArray(currentCategory.children) && currentCategory.children.length));

    let nextCategoryIds;
    if (allFlag) nextCategoryIds = [];
    else if (isLeafCategory && !nextFunctionIds.length) nextCategoryIds = [currentCategory.id];
    else nextCategoryIds = undefined;

    const allowedList = resolveCategoryAttributes(
      categoryId || null,
      categories,
      attributeProfiles
    ).filterAttributes;
    const allowed = new Set(allowedList);
    let nextFacets = facetsFromSearchParams(params, attributeConfig);
    if (allowedList.length) {
      nextFacets = Object.fromEntries(
        Object.entries(nextFacets).filter(([facetId]) => allowed.has(facetId))
      );
    }

    skipNextUrlWrite.current = true;
    setActiveFilters((previous) => {
      const next = {
        ...(previous || {}),
        functionId: nextFunctionIds.length === 1 ? nextFunctionIds[0] : undefined,
        functionIds: nextFunctionIds.length > 1 ? nextFunctionIds : undefined,
        sort: params.get("sort") || "relevance",
        facets: nextFacets,
      };
      if (nextCategoryIds !== undefined) next.categoryIds = nextCategoryIds;
      else if (allFlag) next.categoryIds = [];

      const sameCategory =
        JSON.stringify(previous?.categoryIds || null) === JSON.stringify(next.categoryIds || null);
      const sameFacets = JSON.stringify(previous?.facets || {}) === JSON.stringify(next.facets || {});
      if (
        sameCategory &&
        sameFacets &&
        previous?.functionId === next.functionId &&
        JSON.stringify(previous?.functionIds || null) === JSON.stringify(next.functionIds || null) &&
        previous?.sort === next.sort
      ) {
        return previous;
      }
      return next;
    });
    urlReady.current = true;
  }, [searchKey, attributeConfig, attributeProfiles, categories, categoryId, currentCategory]);

  useEffect(() => {
    if (!urlReady.current) return;
    if (skipNextUrlWrite.current) {
      skipNextUrlWrite.current = false;
      return;
    }
    const next = writeFacetsToSearchParams(new URLSearchParams(searchKey), activeFilters?.facets);
    if (activeFilters?.sort && activeFilters.sort !== "relevance") next.set("sort", activeFilters.sort);
    else next.delete("sort");
    const nextKey = next.toString();
    if (nextKey !== searchKey) {
      lastWrittenSearch.current = nextKey;
      setSearchParams(next, { replace: true });
    }
  }, [activeFilters?.facets, activeFilters?.sort, searchKey, setSearchParams]);

  const handleSelectCategory = useCallback(
    (id) => {
      if (!id || id === categoryId) return;
      setActiveFilters((prev) => ({ ...(prev || {}), categoryIds: [id] }));
      navigate(`/kategoriak/${id}`);
    },
    [navigate, categoryId]
  );

  const hasFunctionFilter =
    (Array.isArray(activeFilters?.functionIds) && activeFilters.functionIds.length > 0) ||
    !!activeFilters?.functionId;

  const isAllMode = Array.isArray(activeFilters?.categoryIds) && activeFilters.categoryIds.length === 0;

  const isLeaf =
    !!(currentCategory && !(Array.isArray(currentCategory.children) && currentCategory.children.length));

  const showFilters = isLeaf || hasFunctionFilter || isAllMode;

  const pageTitle = currentCategory
    ? currentCategory.name
    : hasFunctionFilter
      ? "Funkció szerinti találatok"
      : isAllMode
        ? "Összes termék"
        : "Kategóriák";

  const pageSubtitle = currentCategory
    ? isLeaf
      ? "Termékek"
      : "Alkategóriák"
    : hasFunctionFilter
      ? "A kiválasztott funkciókhoz illeszkedő termékek"
      : isAllMode
        ? "A teljes kínálatból szűrhetsz"
        : "Válassz kategóriát vagy használj szűrőket";

  const activeCount = activeFacetCount(activeFilters) + (hasFunctionFilter ? 1 : 0);

  const sort = activeFilters?.sort || "relevance";
  const setSort = (val) => setActiveFilters((prev) => ({ ...(prev || {}), sort: val }));

  const heroImg = currentCategory?.image || "/img/t5.webp";

  if (loading) return <p className="p-6 text-center">Betöltés...</p>;

  return (
    <>
      <title>{`${pageTitle} – Solarya`}</title>
      <meta name="description" content={(currentCategory?.description || pageSubtitle) || ""} />
      <link rel="canonical" href={categoryCanonical(pathname, searchParams)} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={`${pageTitle} – Solarya`} />
      <meta property="og:description" content={(currentCategory?.description || pageSubtitle) || ""} />
      <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />

      <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
        <section className="relative h-[16rem] md:h-[22rem] overflow-hidden">
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1.05, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{
              backgroundImage: `url(${heroImg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.6)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
          <div className="relative max-w-7xl mx-auto px-6 h-full flex flex-col justify-end pb-8 text-white">
            <motion.h1
              className="text-3xl md:text-5xl font-extrabold drop-shadow-md"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {pageTitle}
            </motion.h1>
            <motion.p
              className="text-lg text-white/90 max-w-2xl"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {pageSubtitle}
            </motion.p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 py-10">
          <div className="mb-4">
            <CategoryBreadcrumb currentCategory={currentCategory} categories={categories} />
          </div>

          {showFilters && (
            <div className="lg:hidden sticky top-14 z-30 mb-4">
              <button
                onClick={() => setDesktopFiltersOpen((v) => !v)}
                className="w-full inline-flex items-center justify-between rounded-xl border bg-white shadow px-4 py-3"
                aria-expanded={desktopFiltersOpen}
                aria-controls="mobile-filter-panel"
              >
                <span className="font-semibold">Szűrők</span>
                <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                  {activeCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-5 h-5 px-2 rounded-full bg-blue-600 text-white text-xs">
                      {activeCount}
                    </span>
                  )}
                  <svg
                    className={`w-4 h-4 transition-transform ${desktopFiltersOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.207l3.71-3.977a.75.75 0 111.1 1.02l-4.24 4.54a.75.75 0 01-1.1 0l-4.24-4.54a.75.75 0 01.02-1.06z" />
                  </svg>
                </span>
              </button>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-8">
            {showFilters && (
              <aside className="lg:w-[19rem] w-full self-start">
                <div className="rounded-2xl border bg-white/90 backdrop-blur shadow-sm">
                  <AnimatePresence initial={false}>
                    {desktopFiltersOpen && (
                      <motion.div
                        id="mobile-filter-panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                      >
                        <div className="p-4">
                          <FilterPanel
                            categoryId={categoryId}
                            activeFilters={activeFilters}
                            setActiveFilters={setActiveFilters}
                            categories={categories}
                            onSelectCategory={handleSelectCategory}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </aside>
            )}

            <main className="flex-1 space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                {showFilters && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Rendezés:</label>
                    <select
                      value={sort}
                      onChange={(e) => setSort(e.target.value)}
                      className="border rounded px-2 py-1 bg-white text-sm"
                    >
                      <option value="relevance">Relevancia</option>
                      <option value="price-asc">Ár: Növekvő</option>
                      <option value="price-desc">Ár: Csökkenő</option>
                      <option value="name-asc">Név: A–Z</option>
                      <option value="name-desc">Név: Z–A</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
                {isLeaf || hasFunctionFilter || isAllMode ? (
                  <ProductGrid activeFilters={activeFilters} />
                ) : (
                  <CategoryGrid categories={currentCategory?.children || categories} />
                )}
              </div>
            </main>
          </div>
        </section>
      </div>
    </>
  );
}
